import { RouteConfig } from 'vue-router';
import MemberRegister from '@views/member/register';
import MemberConfirm from '@views/auth_member/confirm';
import Loading from '@views/loading';
import { LoadingContext } from '@views/loading/context';
import { useLoading as useAuthInitLoading } from '@views/loading/authInitLoading';
import AuthSms from '@views/auth/sms';
import AppEmptyRoute from '@berlin-front/common/components/elements/AppEmptyRoute';
import atoneMemberRegisterFactoryParam from '@views/member/register/atoneMemberRegisterFactoryParam';
import { HEADER_TYPE } from '@berlin-front/common/components/clusters/ModalHeader/composable';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { MemberRegisterContext, useMemberRegisterFactory } from '@views/member/register/composable';
import { AuthMemberConfirmContext } from '@views/auth_member/confirm/context';
import useMemberConfirm from '@views/auth_member/confirm/MemberConfirm';
import { useSmsAuth as useAtoneAuthLogin } from '@views/auth/sms/AtoneAuthLogin';
import { useRegisterSmsAuthWith as AtoneAuthRegisterSms } from '@views/auth/sms/AtoneAuthRegisterSms';
import { useRegisterSmsAuthWith as AtoneAuthGuestRegisterSms } from '@views/auth/sms/AtoneAuthGuestRegisterSms';
import { SmsAuthContext } from '@views/auth/sms/context';
import CompleteView from '@views/auth_complete';
import { AtoneAuthCompleteContext } from '@views/auth_complete/context';
import AtoneAuthComplete from '@views/auth_complete/AtoneAuthComplete';
import AtoneAuthSystemError from '@views/auth_error/system';
import { AtoneAuthSystemErrorViewContext } from '@views/auth_error/system/composable';
import AtoneAuthIntro from '@views/auth_intro';
import { AtoneAuthIntroContext } from '@views/auth_intro/context';
import AuthIntro from '@views/auth_intro/AuthIntro';
import AuthLoginAtoneMemberView from '@views/auth_login/atone';
import AuthLoginNpMemberView from '@views/auth_login/np';
import { AuthLoginAtoneMemberContext } from '@views/auth_login/atone/context';
import { AuthLoginNpMemberContext } from '@views/auth_login/np/context';
import useAtoneAuthAtoneMemberLogin from '@views/auth_login/atone/AtoneAuthAtoneMemberLogin';
import useAtoneAuthNpMemberLogin from '@views/auth_login/np/AtoneAuthNpMemberLogin';
import { AtoneAuthNPMemberRegisterModify } from '@views/member/AtoneAuthNpMemberRegisterContext';
import { includes } from '@berlin-front/common/utils';
import { AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum } from '@netprotections/berlin-bff';
import { ROUTE_NAME } from './route-name';

const atoneAuthRoute: RouteConfig = {
  path: '/atone-auth',
  // データのライフサイクルが複数の画面に跨る場合は、AppEmptyRouteを使用する。
  // ここで定義したコンテキストでデータの持ち回しを行う。
  components: {
    default: AppEmptyRoute,
    contextProvider: MemberRequestContext.provider,
  },
  children: [
    {
      // エラー画面。エラー画面のみ共通ルートに定義される。
      path: '/error/:errorType(system|shop|maintainance)',
      name: ROUTE_NAME.ATONE_AUTH_ERROR_VIEW,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'atoneの認証',
      },
      components: {
        default: AtoneAuthSystemError,
        contextProvider: AtoneAuthSystemErrorViewContext.provider,
      },
      props: {
        default: (route) => ({ errorType: route.params.errorType }),
      },
    },
    {
      // 起動ローディング画面
      path: 'loading',
      name: ROUTE_NAME.ATONEAUTH_INIT_LOADING,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'atoneの認証',
      },
      components: {
        default: Loading,
        contextProvider: LoadingContext.provider,
      },
      props: {
        contextProvider: useAuthInitLoading,
      },
    },
    {
      path: 'intro',
      name: ROUTE_NAME.ATONEAUTH_INTRO,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'atoneの認証',
      },
      components: {
        default: AtoneAuthIntro,
        contextProvider: AtoneAuthIntroContext.provider,
      },
      props: {
        contextProvider: AuthIntro,
      },
    },
    {
      path: 'atone-member-login',
      name: ROUTE_NAME.ATONEAUTH_BY_ATONE_MEMBER,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'atoneの認証',
      },
      components: {
        default: AuthLoginAtoneMemberView,
        contextProvider: AuthLoginAtoneMemberContext.provider,
      },
      props: {
        contextProvider: useAtoneAuthAtoneMemberLogin,
      },
    },
    {
      path: 'sms/atone-member',
      name: ROUTE_NAME.ATONEAUTH_ATONE_MEMBER_SMS_AUTH,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'SMS認証',
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: useAtoneAuthLogin({
          previousPage: ROUTE_NAME.ATONEAUTH_BY_ATONE_MEMBER,
          nextPage: ROUTE_NAME.ATONEAUTH_COMPLETE,
        }),
        default: (route) => {
          const { previous } = route.query;
          return typeof previous === 'string' && includes(...Object.values(ROUTE_NAME))(previous) ? { previous } : {};
        },
      },
    },
    {
      path: 'complete',
      name: ROUTE_NAME.ATONEAUTH_COMPLETE,
      meta: {
        cancelModalType: 'reject',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: '認証完了',
      },
      components: {
        default: CompleteView,
        contextProvider: AtoneAuthCompleteContext.provider,
      },
      props: {
        contextProvider: AtoneAuthComplete,
      },
    },
    {
      // 仮会員の会員登録画面
      path: 'register/guest',
      name: ROUTE_NAME.ATONEAUTH_GUEST_MEMBER_REGISTER,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: '会員登録',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: MemberRegister,
        contextProvider: MemberRegisterContext.provider,
      },
      props: {
        contextProvider: {
          useMemberRegister: useMemberRegisterFactory(
            atoneMemberRegisterFactoryParam({
              nextPage: ROUTE_NAME.ATONEAUTH_GUEST_MEMBER_CONFIRM,
              prevPage: ROUTE_NAME.ATONEAUTH_INTRO,
            }),
          ),
        },
      },
    },
    {
      // 仮会員の会員登録確認画面
      path: 'confirm/guest',
      name: ROUTE_NAME.ATONEAUTH_GUEST_MEMBER_CONFIRM,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: '会員情報の確認',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: MemberConfirm,
        contextProvider: AuthMemberConfirmContext.provider,
      },
      props: {
        contextProvider: useMemberConfirm({
          // 新規、仮会員の場合は遷移先を固定しない
          smsVerificationPage: null,
          registerPage: ROUTE_NAME.ATONEAUTH_GUEST_MEMBER_REGISTER,
        }),
      },
    },
    {
      // 仮会員SMS認証画面
      path: 'sms/exist',
      name: ROUTE_NAME.ATONEAUTH_GUEST_SMS_AUTH,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: 'SMS認証',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: AtoneAuthGuestRegisterSms({
          registerPage: ROUTE_NAME.ATONEAUTH_GUEST_MEMBER_REGISTER,
          nextPage: ROUTE_NAME.ATONEAUTH_COMPLETE,
          registrationRoute: AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum.NUMBER_15,
        }),
      },
    },
    {
      // 新会員登録画面
      path: 'register/new',
      name: ROUTE_NAME.ATONEAUTH_MEMBER_REGISTER,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: '会員登録',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: MemberRegister,
        contextProvider: MemberRegisterContext.provider,
      },
      props: {
        contextProvider: {
          useMemberRegister: useMemberRegisterFactory(
            atoneMemberRegisterFactoryParam({
              nextPage: ROUTE_NAME.ATONEAUTH_MEMBER_CONFIRM,
              prevPage: ROUTE_NAME.ATONEAUTH_INTRO,
            }),
          ),
        },
      },
    },
    {
      // 新会員登録確認画面
      path: 'confirm/new',
      name: ROUTE_NAME.ATONEAUTH_MEMBER_CONFIRM,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: '会員情報の確認',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: MemberConfirm,
        contextProvider: AuthMemberConfirmContext.provider,
      },
      props: {
        contextProvider: useMemberConfirm({
          // 新規、仮会員の場合は遷移先を固定しない
          smsVerificationPage: null,
          registerPage: ROUTE_NAME.ATONEAUTH_MEMBER_REGISTER,
        }),
      },
    },
    {
      // 会員登録SMS認証画面
      path: 'sms/new',
      name: ROUTE_NAME.ATONEAUTH_MEMBER_SMS_AUTH,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: 'SMS認証',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: AtoneAuthRegisterSms({
          registerPage: ROUTE_NAME.ATONEAUTH_MEMBER_REGISTER,
          nextPage: ROUTE_NAME.ATONEAUTH_COMPLETE,
          registrationRoute: AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum.NUMBER_5,
        }),
      },
    },
    {
      path: 'np-member/login',
      name: ROUTE_NAME.ATONEAUTH_BY_NP_MEMBER,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'atoneの認証',
      },
      components: {
        default: AuthLoginNpMemberView,
        contextProvider: AuthLoginNpMemberContext.provider,
      },
      props: {
        contextProvider: useAtoneAuthNpMemberLogin,
      },
    },
    {
      path: 'np-member/register',
      name: ROUTE_NAME.ATONEAUTH_NP_MEMBER_REGISTER,
      meta: {
        cancelModalType: 'shopping',
        headerType: HEADER_TYPE.TITLE_ONLY,
        headerTitle: 'NP会員IDでatoneに連携',
      },
      components: {
        default: MemberRegister,
        contextProvider: MemberRegisterContext.provider,
      },
      props: {
        contextProvider: {
          useMemberRegister: useMemberRegisterFactory(
            atoneMemberRegisterFactoryParam(
              {
                nextPage: ROUTE_NAME.ATONEAUTH_NP_MEMBER_CONFIRM,
                prevPage: ROUTE_NAME.ATONEAUTH_BY_NP_MEMBER,
              },
              AtoneAuthNPMemberRegisterModify,
              true, // skip call API check member type
            ),
          ),
        },
      },
    },
    {
      // 会員登録確認画面
      path: 'np-member/confirm',
      name: ROUTE_NAME.ATONEAUTH_NP_MEMBER_CONFIRM,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: '会員情報の確認',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: MemberConfirm,
        contextProvider: AuthMemberConfirmContext.provider,
      },
      props: {
        contextProvider: useMemberConfirm({
          smsVerificationPage: ROUTE_NAME.ATONEAUTH_NP_MEMBER_SMS_AUTH,
          registerPage: ROUTE_NAME.ATONEAUTH_NP_MEMBER_REGISTER,
        }),
      },
    },
    {
      // 会員登録確認画面
      path: 'np-member/sms-auth',
      name: ROUTE_NAME.ATONEAUTH_NP_MEMBER_SMS_AUTH,
      meta: {
        cancelModalType: 'shopping',
        headerTitle: 'SMS認証',
        headerType: HEADER_TYPE.TITLE_ONLY,
      },
      components: {
        default: AuthSms,
        contextProvider: SmsAuthContext.provider,
      },
      props: {
        contextProvider: AtoneAuthRegisterSms({
          registerPage: ROUTE_NAME.ATONEAUTH_NP_MEMBER_REGISTER,
          nextPage: ROUTE_NAME.ATONEAUTH_COMPLETE,
          registrationRoute: AtoneMemberRegisterPostRequestMemberRegistrationRouteEnum.NUMBER_6,
        }),
      },
    },
  ],
};

export default atoneAuthRoute;
